<script>
  import LetterTile from './LetterTile.svelte'
  import { unlockAudio, play, playSequence, hasOverride, overrideDuration } from './audio.js'
  import { celebrate } from './celebration.js'
  import { Link2 } from '@lucide/svelte'

  // Two tree branches (left + right). The 🐒 monkey and 🦜 parrot start perched
  // together on the LEFT branch.
  //   Stage 1: tap each sound → the monkey jumps from the branch onto that sound.
  //            When every sound is tapped, the tiles join into the whole word and
  //            the monkey hops to the RIGHT branch.
  //   Stage 2: tap the word → it plays blended and the parrot flies left→right.
  //
  // Coach / training layer: after 3s idle, or on an unproductive tap (a gap, or
  // re-tapping a sound that's already done), a bouncing 👆 hand + a speech bubble
  // point to what to do next (next sound → the whole word → the ▶ arrow).
  let { word, oncomplete, cvBlend = false, onblendsaved } = $props()

  let stage = $state(1)
  let tapped = $state(new Set())
  let activeRange = $state(null) // [start, end] tile range currently highlighted
  let selRange = $state(null)    // [start, end] tiles selected while editing a blend
  let played = $state(false)
  let hint = $state(null) // { target: number | 'word' | 'next', text }

  let areaEl, rowEl, monkeyEl, monkeyHopEl, parrotEl, leftBranchEl, rightBranchEl, coachEl
  let timers = []
  let idleTimer = null
  let advancing = false

  const N = () => word.sounds.length
  $effect(() => { word; reset(); return () => { clearTimers(); clearIdle() } })
  let links = $state(new Set()) // gap index i means tile i and i+1 are blended together

  const spelling = () => word.sounds.map((s) => s.text).join('')
  // Saved blend config for this word (which adjacent sounds are joined). Empty until
  // the tutor connects letters with the Blend tool; once saved it's used always.
  function loadLinks() {
    try { const raw = localStorage.getItem('blend:' + spelling()); if (raw) return new Set(JSON.parse(raw)) } catch (_) { /* none */ }
    return new Set()
  }
  function saveLinks() { try { localStorage.setItem('blend:' + spelling(), JSON.stringify([...links])) } catch (_) {} }
  // Blend editing: select a CONTIGUOUS run of tiles, then Blend & Save.
  function selectTile(i) {
    if (!selRange) { selRange = [i, i]; return }
    const [a, b] = selRange
    if (i === b + 1) selRange = [a, i]            // extend right
    else if (i === a - 1) selRange = [i, b]       // extend left
    else if (i === b && a < b) selRange = [a, b - 1] // shrink right
    else if (i === a && a < b) selRange = [a + 1, b] // shrink left
    else if (i === a && a === b) selRange = null  // tap the single selected → clear
    else selRange = [i, i]                        // non-adjacent → start over (keeps it a run, no gaps)
  }
  function blendSave() {
    if (!selRange || selRange[1] <= selRange[0]) return
    const n = new Set(links)
    for (let k = selRange[0]; k < selRange[1]; k++) n.add(k)
    links = n; saveLinks(); selRange = null
    onblendsaved?.()
  }
  function clearBlends() { links = new Set(); saveLinks(); selRange = null; onblendsaved?.() }

  function clearTimers() { timers.forEach(clearTimeout); timers = [] }
  function clearIdle() { if (idleTimer) { clearTimeout(idleTimer); idleTimer = null } }
  function armIdle() { clearIdle(); idleTimer = setTimeout(fireHint, 3000) } // hint if idle 3s

  function reset() {
    clearTimers(); clearIdle()
    stage = 1; tapped = new Set(); activeRange = null; selRange = null; advancing = false; played = false; hint = null
    links = loadLinks()
    if (parrotEl) parrotEl.style.animation = 'none'
    requestAnimationFrame(parkOnLeft)
    armIdle()
  }

  function tiles() { return rowEl ? [...rowEl.querySelectorAll('[data-index]')] : [] }
  // X (relative to the area) of a fraction across an element's width.
  function spanX(el, frac = 0.5) {
    const a = areaEl.getBoundingClientRect(), r = el.getBoundingClientRect()
    return r.left + r.width * frac - a.left
  }
  const centerX = (el) => spanX(el, 0.5)

  function placeMonkey(x) { if (monkeyEl) { monkeyEl.style.transform = `translate(calc(-50% + ${x}px), 0)`; monkeyEl.style.opacity = '1' } }
  function placeParrot(x) { if (parrotEl) { parrotEl.style.transform = `translateX(calc(-50% + ${x}px))`; parrotEl.style.opacity = '1' } }

  function parkOnLeft() {
    if (!leftBranchEl) return
    placeMonkey(spanX(leftBranchEl, 0.34))   // monkey a little left on the branch
    placeParrot(spanX(leftBranchEl, 0.68))   // parrot beside it
  }

  function bounce() {
    if (monkeyHopEl) { monkeyHopEl.classList.remove('hop'); void monkeyHopEl.offsetWidth; monkeyHopEl.classList.add('hop') }
  }
  function hopMonkeyTo(x) { placeMonkey(x); bounce() }

  const blendKey = (codes) => 'bl:' + codes.join('_')
  const codeOf = (s) => s.code || String(s.audioKey || '').split(':').pop()
  // Stage-2 (parrot) plan: a saved blend run plays its recorded clip (bl:<codes>)
  // when one exists, otherwise its stitched phonemes. `ranges` maps each played
  // key back to the tile(s) it covers, for the highlight sweep.
  function blendPlan() {
    const keys = [], ranges = []
    let i = 0
    while (i < word.sounds.length) {
      let j = i
      while (j < word.sounds.length - 1 && links.has(j)) j++
      const bk = blendKey(word.sounds.slice(i, j + 1).map(codeOf))
      if (j > i && hasOverride(bk)) { keys.push(bk); ranges.push([i, j]) }
      else { for (let k = i; k <= j; k++) { keys.push(word.sounds[k].blendKey || word.sounds[k].audioKey); ranges.push([k, k]) } }
      i = j + 1
    }
    return { keys, ranges }
  }

  // ---------- Coach ----------
  function nextUntapped() { for (let i = 0; i < N(); i++) if (!tapped.has(i)) return i; return -1 }
  function clearHint() { hint = null }
  function computeHint() {
    if (cvBlend) return null // no coach while editing a blend
    if (stage === 1) {
      const i = nextUntapped()
      return i < 0 ? null : { target: i, text: tapped.size ? 'Tap this sound' : 'Tap the first sound' }
    }
    if (!played) return { target: 'word', text: 'Now tap the whole word!' }
    return null // word done → App auto-advances after 3s, no nudge needed
  }
  function fireHint() {
    hint = computeHint()
    if (hint) requestAnimationFrame(positionCoach)
  }
  function positionCoach() {
    if (!hint || !coachEl || !areaEl) return
    const target = typeof hint.target === 'number' ? tiles()[hint.target] : rowEl
    if (!target) return
    const a = areaEl.getBoundingClientRect(), r = target.getBoundingClientRect()
    const gap = Math.round(window.innerHeight * 0.02)
    coachEl.style.left = `${r.left + r.width / 2 - a.left}px`
    coachEl.style.top = `${r.bottom - a.top + gap}px`
  }

  // ---------- Stage 1 ----------
  function tapSound(i) {
    play(word.sounds[i].audioKey)          // monkey sounds out each letter on its own
    activeRange = [i, i]
    const n = new Set(tapped); n.add(i); tapped = n
    const t = tiles()[i]; if (t) hopMonkeyTo(centerX(t))
    clearHint()
    if (tapped.size === N() && stage === 1 && !advancing) {
      advancing = true; clearIdle()
      timers.push(setTimeout(goStage2, 650))
    } else {
      armIdle()
    }
  }

  function goStage2() {
    stage = 2; activeRange = null
    requestAnimationFrame(() => {
      if (rightBranchEl) hopMonkeyTo(spanX(rightBranchEl, 0.34)) // monkey crosses to the far tree
    })
    clearHint(); armIdle()
  }

  // ---------- Stage 2 ----------
  function playWord() {
    const ts = tiles()
    if (!ts.length || !leftBranchEl || !rightBranchEl) return
    clearTimers()
    let times, totalMs, ranges = null
    let usePhonic = false
    try { usePhonic = localStorage.getItem('phonicword:' + word.wordAudioKey) === '1' } catch (_) {}
    if (!usePhonic && hasOverride(word.wordAudioKey)) {
      play(word.wordAudioKey)                                    // recorded whole word
      const n = word.sounds.length || 1
      totalMs = Math.round(overrideDuration(word.wordAudioKey) * 1000) || 800
      times = word.sounds.map((_, i) => ({ i, atMs: Math.round((i * totalMs) / n) })) // sweep the highlight
    } else {
      const plan = blendPlan()                                   // parrot blends — recorded blend clips where saved
      const seq = playSequence(plan.keys)
      times = seq.times; totalMs = seq.totalMs; ranges = plan.ranges
    }
    const dur = Math.max(600, totalMs)
    if (parrotEl) {
      const fromX = spanX(leftBranchEl, 0.68), toX = spanX(rightBranchEl, 0.68)
      parrotEl.style.setProperty('--fromX', `calc(-50% + ${fromX}px)`)
      parrotEl.style.setProperty('--midX', `calc(-50% + ${(fromX + toX) / 2}px)`)
      parrotEl.style.setProperty('--toX', `calc(-50% + ${toX}px)`)
      parrotEl.style.opacity = '1'
      parrotEl.style.animation = 'none'; void parrotEl.offsetWidth
      parrotEl.style.animation = `fly ${dur}ms ease-in-out forwards`
    }
    times.forEach((t) => timers.push(setTimeout(() => { activeRange = ranges ? (ranges[t.i] || [t.i, t.i]) : [t.i, t.i] }, t.atMs)))
    timers.push(setTimeout(() => { activeRange = null; celebrate(rowEl) }, dur + 120))
    played = true
    clearHint(); oncomplete?.(); armIdle() // whole word played → unlock "next"
  }

  function onTap(e) {
    unlockAudio()
    if (stage === 1) {
      const el = e.target.closest('[data-index]')
      if (!el) { if (!cvBlend) fireHint(); return }
      const i = +el.dataset.index
      if (cvBlend) { selectTile(i); return }                           // edit mode: pick letters to blend
      if (tapped.has(i)) { play(word.sounds[i].audioKey); fireHint(); return } // re-hear that single sound
      tapSound(i)
    } else {
      playWord()
    }
  }
</script>

<div class="area" bind:this={areaEl}>
  <div class="branch left" bind:this={leftBranchEl}></div>
  <div class="branch right" bind:this={rightBranchEl}></div>

  <div class="monkey" bind:this={monkeyEl}><span class="hopper" bind:this={monkeyHopEl}>🐒</span></div>
  <div class="parrot" bind:this={parrotEl}>🦜</div>

  <div
    class="row" class:joined={stage === 2} class:hinting={hint?.target === 'word'}
    bind:this={rowEl} onpointerdown={onTap} role="group" aria-label="Tap each sound, then tap the word"
  >
    {#each word.sounds as s, i}
      <LetterTile letter={s.text} index={i} active={!!activeRange && i >= activeRange[0] && i <= activeRange[1]} played={tapped.has(i)} hint={hint?.target === i} sel={cvBlend && !!selRange && i >= selRange[0] && i <= selRange[1]} />
      {#if stage === 1 && i < word.sounds.length - 1 && links.has(i)}
        <span class="link" aria-hidden="true"></span>
      {/if}
    {/each}
  </div>

  <div class="coach" class:show={!!hint} bind:this={coachEl}>
    <div class="hand">👆</div>
    <div class="bubble">{hint?.text ?? ''}</div>
  </div>

  {#if cvBlend}
    <div class="blendbar">
      {#if selRange && selRange[1] > selRange[0]}
        <button class="bsave" onpointerdown={(e) => { e.stopPropagation(); blendSave() }}><Link2 size={18} /> Blend &amp; Save</button>
      {:else}
        <span class="bhint">Tap 2+ letters in a row to blend</span>
      {/if}
      {#if links.size}<button class="bclear" onpointerdown={(e) => { e.stopPropagation(); clearBlends() }}>Clear blends</button>{/if}
    </div>
  {/if}
</div>

<style>
  .area { position: relative; padding: 13vh 16vh 0; }

  /* Tree branches the animals perch on — one at each end. */
  .branch {
    position: absolute; top: 9vh; width: 12vh; height: 2.4vh;
    border-radius: 999px;
    background: linear-gradient(#9a6533, #6d4520);
    box-shadow: 0 3px 0 rgba(0, 0, 0, 0.22);
  }
  .branch.left { left: 1.5vh; }
  .branch.right { right: 1.5vh; }
  .branch::before, .branch::after {
    content: '🍃'; position: absolute; top: -1.7vh; font-size: 2.6vh;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.25));
  }
  .branch.left::before { left: -0.6vh; }
  .branch.left::after { left: 3.4vh; top: -1.2vh; font-size: 2.1vh; }
  .branch.right::before { right: -0.6vh; }
  .branch.right::after { right: 3.4vh; top: -1.2vh; font-size: 2.1vh; }

  .monkey, .parrot {
    position: absolute; left: 0; font-size: 8vh; line-height: 1;
    pointer-events: none; will-change: transform; opacity: 0;
    filter: drop-shadow(0 3px 4px rgba(0, 0, 0, 0.35));
  }
  .monkey { top: 1.4vh; transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease; }
  .parrot { top: 2vh; }
  .hopper { display: inline-block; }
  /* `.hop` is toggled from JS and `fly` is started from JS, so both must be
     global — otherwise Svelte scopes/prunes them and the animations never run. */
  .hopper:global(.hop) { animation: hop 0.42s ease; }
  @keyframes -global-hop {
    0% { transform: translateY(0); }
    45% { transform: translateY(-5vh); }
    100% { transform: translateY(0); }
  }
  @keyframes -global-fly {
    0% { transform: translateX(var(--fromX)); }
    50% { transform: translateX(var(--midX)) translateY(-7vh); }
    100% { transform: translateX(var(--toX)); }
  }

  .row {
    display: flex; gap: 3vh; justify-content: center;
    transition: gap 0.5s ease; cursor: pointer; touch-action: manipulation;
  }
  .row.joined { gap: 0.2vh; }
  .row.hinting { animation: rowpulse 0.9s ease-in-out infinite; }
  @keyframes rowpulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.03); } }

  /* Coach: bouncing hand + speech bubble that points at the next thing to tap. */
  .coach {
    position: absolute; left: 0; top: 0; transform: translateX(-50%);
    display: flex; flex-direction: column; align-items: center; gap: 0.3vh;
    pointer-events: none; opacity: 0; transition: opacity 0.25s ease; z-index: 14;
  }
  .coach.show { opacity: 1; }
  .coach .hand { font-size: 4.4vh; animation: bob 0.85s ease-in-out infinite; filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.3)); }
  .coach .bubble {
    background: #fffef0; color: #2e5d1f; font-weight: 800; font-size: 2vh;
    padding: 0.7vh 1.6vh; border-radius: 999px; box-shadow: 0 4px 10px rgba(0, 0, 0, 0.22);
    white-space: nowrap;
  }
  @keyframes bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-1vh); } }

  /* CV blend "stitch" clasp between two sounds — a vertical capsule with round
     end-caps (tap to blend/unblend). On = joined. */
  .link {
    align-self: center; flex: none; position: relative; padding: 0; pointer-events: none;
    width: 1.6vh; height: 6vh; border-radius: 999px; background: #2e5d1f;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18); z-index: 8;
  }
  .link::before, .link::after {
    content: ''; position: absolute; left: 50%; transform: translateX(-50%);
    width: 2.8vh; height: 2.8vh; border-radius: 50%; background: inherit;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
  }
  .link::before { top: -1vh; }
  .link::after { bottom: -1vh; }

  .blendbar { position: absolute; left: 0; right: 0; bottom: -6vh; display: flex; gap: 1.4vh; align-items: center; justify-content: center; }
  .bhint { color: #fffef0; font-weight: 800; font-size: 1.9vh; opacity: 0.95; }
  .bsave { display: inline-flex; align-items: center; gap: 0.6vh; border: none; background: #ff7043; color: #fff; font-family: inherit; font-weight: 800; font-size: 2vh; padding: 1.2vh 2.4vh; border-radius: 999px; box-shadow: 0 4px 0 rgba(0, 0, 0, 0.2); cursor: pointer; }
  .bsave:active { transform: translateY(2px); box-shadow: 0 2px 0 rgba(0, 0, 0, 0.2); }
  .bclear { border: none; background: rgba(255, 255, 255, 0.9); color: #b3271b; font-family: inherit; font-weight: 800; font-size: 1.7vh; padding: 1vh 1.8vh; border-radius: 999px; cursor: pointer; }
</style>
